import { useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import Implement from 'components/Implement';

import useAccessToken from 'hooks/useAccessToken';
import useCurrentOrganization from 'hooks/useCurrentOrganization';
import useGlobalStore from 'hooks/useGlobalStore';
import usePageRoutes from 'hooks/usePageRoutes';

const UserOrganizationGuard = ({ onPassed }: { onPassed?: (passed: boolean) => void }) => {
    const navigate = useNavigate();
    const { onboardingOrganizationCreationPage } = usePageRoutes();
    const { fetchCurrentOrganization, isLoading } = useCurrentOrganization();
    const { decodedToken } = useAccessToken();
    const fetchAttemptedRef = useRef(false);

    const { getValue } = useGlobalStore({
        namespace: 'useCurrentOrganization'
    });

    const currentOrganization = getValue('currentOrganization');

    const hasOrganizationInToken = Boolean(
        decodedToken &&
        (decodedToken.org_id || (decodedToken as { organization_id?: string }).organization_id)
    );

    // Single effect sequences fetch-kickoff and redirect-on-empty so the
    // navigate can only fire on a later render tick, after isLoading or
    // currentOrganization has settled. The previous two-effect split had
    // effect 2 read fetchAttemptedRef.current (just mutated by effect 1)
    // against isLoading=false from a stale closure, redirecting before
    // the fetch returned — which then ping-ponged against OnboardingGuard
    // (token has org_id → OnboardingGuard sends user back here) forever.
    useEffect(() => {
        if (currentOrganization || isLoading) {
            return;
        }

        if (!fetchAttemptedRef.current) {
            fetchAttemptedRef.current = true;
            fetchCurrentOrganization();

            return;
        }

        // hasOrganizationInToken gate: if the token says the user has an org
        // but the API fetch returned empty, the two guards would otherwise
        // disagree (OnboardingGuard trusts the token, this guard would not)
        // and loop. Stay in Loading until the two signals agree.
        if (hasOrganizationInToken) {
            return;
        }

        navigate(onboardingOrganizationCreationPage, { replace: true });
    }, [
        currentOrganization,
        isLoading,
        hasOrganizationInToken,
        fetchCurrentOrganization,
        navigate,
        onboardingOrganizationCreationPage
    ]);

    if (currentOrganization) {
        onPassed?.(true);

        return <Outlet />;
    }

    return (
        <Implement>
            <p>UserOrganizationGuard - Loading...</p>
        </Implement>
    );
};

export default UserOrganizationGuard;
